import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Register() {
    const { t, lang } = useLang();
    const { register } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await register(form.email, form.password, form.name, lang);
            toast.success("Welcome!");
            const pendingInvite = sessionStorage.getItem("vivoz_pending_invite");
            const pendingReferral = sessionStorage.getItem("vivoz_pending_referral");
            nav(pendingInvite ? `/join/${pendingInvite}` : pendingReferral ? `/r/${pendingReferral}` : "/dashboard");
        } catch (err) {
            toast.error(err.response?.data?.detail || t("common.error"));
        } finally {
            setLoading(false);
        }
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <div className="min-h-screen bg-ink text-slate-900">
            <Navbar />
            <div className="max-w-md mx-auto px-6 pt-20 pb-24">
                <div className="mono-label mb-4">/ SIGN UP</div>
                <h1 className="font-display text-4xl md:text-5xl mb-3 tracking-tight" data-testid="register-title">
                    {t("auth.register_title")}
                </h1>
                <p className="text-slate-600 mb-10">{t("auth.register_sub")}</p>
                <form onSubmit={submit} className="space-y-5">
                    <div>
                        <label className="mono-label block mb-2">{t("auth.name")}</label>
                        <input
                            data-testid="register-name-input"
                            type="text" required value={form.name} onChange={set("name")}
                            className="w-full bg-transparent border border-slate-300 px-4 py-3 rounded-sm focus:border-brand outline-none transition-colors"
                        />
                    </div>
                    <div>
                        <label className="mono-label block mb-2">{t("auth.email")}</label>
                        <input
                            data-testid="register-email-input"
                            type="email" required value={form.email} onChange={set("email")}
                            className="w-full bg-transparent border border-slate-300 px-4 py-3 rounded-sm focus:border-brand outline-none transition-colors"
                        />
                    </div>
                    <div>
                        <label className="mono-label block mb-2">{t("auth.password")}</label>
                        <input
                            data-testid="register-password-input"
                            type="password" required minLength={6} value={form.password} onChange={set("password")}
                            className="w-full bg-transparent border border-slate-300 px-4 py-3 rounded-sm focus:border-brand outline-none transition-colors"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary w-full justify-center"
                        data-testid="register-submit-btn"
                    >
                        {loading ? "..." : t("auth.register_btn")}
                    </button>
                </form>
                <div className="flex items-center gap-4 my-8">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="mono-label text-slate-400">{t("auth.or")}</span>
                    <div className="flex-1 h-px bg-slate-200" />
                </div>
                <GoogleSignInButton />
                <p className="mt-8 text-sm text-slate-500">
                    {t("auth.has_account")}{" "}
                    <Link to="/login" className="text-brand hover:underline" data-testid="register-go-login">
                        {t("nav.login")}
                    </Link>
                </p>
            </div>
        </div>
    );
}
